import { z } from 'zod';
import { arrangementNoticeSchema } from './arrangementNotice.schema';
import { objectIdSchema } from './common.schema';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  THE WIRE FOR A PER-OFFICER DUTY NOTIFICATION. [C3c.1 §6d]
 *
 *  ⚠️ IT CARRIES NO OFFICER ID AND NO PHONE. A notification is addressed to the reader, so the
 *  only officer it could name is the one holding the token — and `loadAppForceOfficers`' rule
 *  is that the absence from the projection and the absence from the type are ONE mechanism.
 *
 *  ⚠️ AND NO `read` / `unread` ANYWHERE, ON THE WIRE OR IN THE COLUMN. `notifications.test.ts`
 *  CLAIM 1 greps every model for `read_at|is_read|unread` and it is right to: the BELL's state
 *  is derived and must stay so. This is the other mechanism, and it says `seen` — the word
 *  `officers.notifications_seen_at` already uses for the same act one surface over.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const dutyNotificationSchema = z.object({
  id: objectIdSchema,
  arrangementId: objectIdSchema,
  deploymentId: objectIdSchema,
  /** What the order is called, derived on read — never a stamped title ([D4.5 §3d]). */
  arrangementTitle: z.string(),
  /** `DP1 · Esplanade`, the grain an officer recognises. */
  dutyPointTitle: z.string(),
  /** A business date, `YYYY-MM-DD` — [P3a]'s rule, so a midnight-spanning shift survives it. */
  dutyDate: z.string(),
  /** Already rendered by the server, so the app formats no times. */
  slotLabel: z.string(),
  /** When it arrived. The list sorts on this, newest first. */
  createdAt: z.string(),
  /**
   * ⚠️ A REQUIRED KEY WITH A NULLABLE VALUE. The row renders a "new" dot either way, so `null`
   * answers a question the screen is asking — §6's own reading of [P6d]'s optional-vs-nullable
   * test.
   */
  seenAt: z.string().nullable(),
});

export const dutyNotificationsSchema = z.object({
  /** ⚠️ EXACT, and independent of `DUTY_NOTIFICATION_LIMIT` — the bell's own rule. */
  total: z.number().int().min(0),
  /** How many of `total` the officer has not opened yet. Drives the bell's badge. */
  unseenCount: z.number().int().min(0),
  items: z.array(dutyNotificationSchema),
  /**
   * ⚠️ [P7B §6b] "AN ORDER YOU WERE ON WAS TAKEN BACK TO DRAFT BY A SUPER ADMIN" — a second, stored fact
   * of the same family (something this officer was TOLD), carried here so the bell costs no second
   * request. OPTIONAL and omitted when the officer has none, so the response is byte-identical for
   * everyone a revert never reached; `noticeUnseenCount` beside it, and the badge sums both.
   */
  notices: z.array(arrangementNoticeSchema).optional(),
  noticeUnseenCount: z.number().int().min(0).optional(),
});

/**
 * ⚠️ §6b NARROWS THE SAME VERB RATHER THAN ADDING A SECOND ONE — `completeShareSchema`'s
 * `divisionOrgUnitId` verbatim, and for its reason: a super admin's inbox row is about ONE
 * division's share, and a schema cannot validate it because a schema cannot see `Scope`.
 */
export const notifyNamedSchema = z.object({
  divisionOrgUnitId: objectIdSchema.optional(),
});

/** What a send reports back. ⚠️ COUNTS, NEVER NAMES — see `dutyNotificationSchema`. */
export const notifyResultSchema = z.object({
  notified: z.number().int().min(0),
});
